



























































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { mapGetters, mapState } from 'vuex';
import { Nullable, unreachableCase } from 'frontend/types';
import { LessonUserReportInputs, LessonUserReportResult } from 'frontend/types/lessons/requests';
import { lessonBaseUrlParams } from 'frontend/views/lessons/editor/utils';
import { Lesson } from 'frontend/types/lessons/lesson';
import { debounce } from 'debounce';
import { nativeBridgeIsPresent, sendMessageAcrossNativeBridge } from 'frontend/utils/nativeBridge';

type PopoverState = 'default' | 'subcategories' | 'description' | 'done';
type CategoryChoice = 'technical' | 'content';
type SubcategoryChoice =
  | 'typo'
  | 'visuals_interactives_broken'
  | 'dispute'
  | 'term_help'
  | 'clarification'
  | 'too_fast'
  | 'too_slow';
interface SubcategoryChoiceText {
  technical: SubcategoryChoice[];
  content: SubcategoryChoice[];
}
type SubcategoryText = {
  [key in SubcategoryChoice]: {
    choiceLabel: string;
    descriptionLabel: string;
  };
};

interface ProblemReportStorageValue {
  categoryChoice: CategoryChoice;
  subcategoryChoice: SubcategoryChoice;
  description: string;
}

@Component({
  computed: {
    ...mapState('lesson', ['lesson', 'showingReportProblem']),
    ...mapGetters('pageHeader', ['isMobile']),
  },
})
export default class ReportProblem extends Vue {
  private readonly lesson!: Lesson;
  private readonly showingReportProblem!: boolean;

  private popoverState: PopoverState = 'default';
  private categoryChoice: Nullable<CategoryChoice> = null;
  private subcategoryChoice: Nullable<SubcategoryChoice> = null;
  private description = '';
  private isSendingReport = false;
  private showDraftSavedMessage = false;
  private showDraftSavedTooltip = false;
  private errorMessage: Nullable<string> = null;

  private subcategoryChoicesByCategory: SubcategoryChoiceText = {
    technical: ['typo', 'visuals_interactives_broken', 'dispute'],
    content: ['term_help', 'clarification', 'too_fast', 'too_slow'],
  };

  private subcategoryText: SubcategoryText = {
    typo: {
      choiceLabel: 'There is a typo.',
      descriptionLabel: 'Please specify which word or phrase has a typo:',
    },
    visuals_interactives_broken: {
      choiceLabel: 'The visuals or interactives aren’t rendering properly.',
      descriptionLabel: 'Please describe exactly what isn’t rendering properly:',
    },
    dispute: {
      choiceLabel: 'This content is incorrect.',
      descriptionLabel: 'Please specify what is incorrect:',
    },
    term_help: {
      choiceLabel: 'I don’t know what the definition of a term is.',
      descriptionLabel: 'Please specify which term is unclear to you:',
    },
    clarification: {
      choiceLabel: 'This content is not clearly stated or is confusing.',
      descriptionLabel: 'Please specify which word or phrase is unclear and explain why:',
    },
    too_fast: {
      choiceLabel: 'This lesson is moving way too fast!',
      descriptionLabel: 'Please explain which part is moving too fast:',
    },
    too_slow: {
      choiceLabel: 'This lesson is moving way too slow or is repetitive!',
      descriptionLabel: 'Please explain which part is moving too slow or is repetitive:',
    },
  };

  private get storageKey(): string {
    const x = ['reportproblem', this.$route.params.lessonSlug, this.$route.params.blockIndex].join(
      '__',
    );
    return x;
  }

  private getFromStorage(): Nullable<ProblemReportStorageValue> {
    try {
      const results = JSON.parse(localStorage.getItem(this.storageKey) || '');
      if (results.categoryChoice || (results.subcategoryChoice && results.description)) {
        return results;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  private saveToStorage(): boolean {
    if (this.categoryChoice && this.subcategoryChoice && this.description.length) {
      try {
        localStorage.setItem(
          this.storageKey,
          JSON.stringify({
            categoryChoice: this.categoryChoice,
            subcategoryChoice: this.subcategoryChoice,
            description: this.description,
          }),
        );
        return true;
      } catch (e) {
        // ignore
      }
    }
    return false;
  }

  private clearStorage() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (e) {
      // noop
    }
  }

  @Watch('description')
  private descriptionChanged(newDescription: string, oldDescription: string) {
    // This check prevents the "Draft saved" message from showing when rehydrating from storage
    if (oldDescription.trim() !== '') {
      this.handleDescriptionChanged();
    }
    if (newDescription.trim() === '') {
      this.clearStorage();
    }
  }

  private get handleDescriptionChanged() {
    return debounce(() => {
      this.showDraftSavedMessage = this.saveToStorage() && !this.isSendingReport;
    }, 1000);
  }

  private handleShowPopover() {
    const storageData = this.getFromStorage();
    if (storageData) {
      this.categoryChoice = storageData.categoryChoice;
      this.subcategoryChoice = storageData.subcategoryChoice;
      this.description = storageData.description;
      this.popoverState = 'description';
    }
  }

  private handleClosePopover() {
    this.categoryChoice = null;
    this.subcategoryChoice = null;
    this.popoverState = 'default';
    this.isSendingReport = false;
    this.showDraftSavedMessage = false;
    this.errorMessage = null;
    this.description = '';
    this.$store.dispatch('lesson/hideReportProblem');
  }

  private closePopoverWithoutClearing() {
    this.$store.dispatch('lesson/hideReportProblem');
  }

  private handleChooseCategory() {
    if (this.categoryChoice === 'technical' && nativeBridgeIsPresent()) {
      this.handleClosePopover();
      // Ensure the popover is completely closed before notifiying the client.
      setTimeout(() => sendMessageAcrossNativeBridge('reportBug'), 50);
    } else {
      this.popoverState = 'subcategories';
    }
  }

  private handleChooseSubcategory() {
    this.popoverState = 'description';
  }

  private handleBackButton() {
    if (this.popoverState === 'subcategories') {
      this.popoverState = 'default';
      this.categoryChoice = null;
      this.subcategoryChoice = null;
    } else if (this.popoverState === 'description') {
      this.popoverState = 'subcategories';
      this.subcategoryChoice = null;
      this.description = '';
      this.errorMessage = null;
      this.clearStorage();
    } else if (this.popoverState === 'default' || this.popoverState === 'done') {
      // not possible / don't care
    } else {
      unreachableCase(this.popoverState);
    }
  }

  private async handleSubmitReport() {
    const minDescriptionLength = 10;
    this.errorMessage = null;
    if (this.description.length < minDescriptionLength) {
      this.errorMessage = `Must contain at least ${minDescriptionLength} characters`;
      return;
    }
    if (this.subcategoryChoice) {
      this.isSendingReport = true;
      const input: LessonUserReportInputs = {
        params: lessonBaseUrlParams(this.$route),
        data: {
          block_index: this.$route.params.blockIndex || '999',
          version_name: this.lesson.version_name,
          selected_choice: this.subcategoryChoice,
          description: this.description,
        },
      };
      const result = (await this.$store.dispatch(
        'lesson/userReport',
        input,
      )) as LessonUserReportResult;
      if (result.success) {
        this.popoverState = 'done';
        this.clearStorage();
      } else {
        this.errorMessage = 'There was a problem submitting this feedback.';
        this.isSendingReport = false;
      }
    }
  }
}
